import { PaymentsConfig, PricingPlan } from '../types';

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhoneNumber = (phone: string): boolean => {
  // Basic phone number validation - can be enhanced based on requirements
  const phoneRegex = /^\+?[\d\s-()]{10,}$/;
  return phoneRegex.test(phone);
};

export const validatePaymentsConfig = (
  config: PaymentsConfig
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!config.publishableKey) {
    errors.push('publishableKey is required');
  }

  if (!config.provider) {
    errors.push('provider is required');
  }

  if (config.provider !== 'stripe') {
    errors.push('Only stripe provider is currently supported');
  }

  if (!config.environment) {
    errors.push('environment is required');
  }

  if (
    config.environment &&
    !['development', 'production'].includes(config.environment)
  ) {
    errors.push('environment must be either "development" or "production"');
  }

  if (config.publishableKey && !config.publishableKey.startsWith('pk_')) {
    errors.push('publishableKey must start with "pk_"');
  }

  if (config.secretKey && !config.secretKey.startsWith('sk_')) {
    errors.push('secretKey must start with "sk_"');
  }

  if (config.webhookSecret && !config.webhookSecret.startsWith('whsec_')) {
    errors.push('webhookSecret must start with "whsec_"');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validatePricingPlan = (
  plan: PricingPlan
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!plan.id) {
    errors.push('id is required');
  }

  if (!plan.name) {
    errors.push('name is required');
  }

  if (typeof plan.price !== 'number' || plan.price < 0) {
    errors.push('price must be a non-negative number');
  }

  if (!plan.currency) {
    errors.push('currency is required');
  }

  if (!plan.interval) {
    errors.push('interval is required');
  }

  if (!['day', 'week', 'month', 'year'].includes(plan.interval)) {
    errors.push('interval must be one of: day, week, month, year');
  }

  if (!plan.stripePriceId) {
    errors.push('stripePriceId is required');
  }

  if (plan.stripePriceId && !plan.stripePriceId.startsWith('price_')) {
    errors.push('stripePriceId must start with "price_"');
  }

  if (!Array.isArray(plan.features)) {
    errors.push('features must be an array');
  }

  if (
    plan.intervalCount &&
    (typeof plan.intervalCount !== 'number' || plan.intervalCount < 1)
  ) {
    errors.push('intervalCount must be a positive number');
  }

  if (
    plan.trialPeriodDays &&
    (typeof plan.trialPeriodDays !== 'number' || plan.trialPeriodDays < 0)
  ) {
    errors.push('trialPeriodDays must be a non-negative number');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateStripeId = (
  id: string,
  type: 'customer' | 'subscription' | 'price' | 'product' | 'payment_intent'
): boolean => {
  const prefixes = {
    customer: 'cus_',
    subscription: 'sub_',
    price: 'price_',
    product: 'prod_',
    payment_intent: 'pi_',
  };

  return id.startsWith(prefixes[type]);
};

export const validateAmount = (
  amount: number,
  currency: string
): { isValid: boolean; error?: string } => {
  if (typeof amount !== 'number') {
    return { isValid: false, error: 'Amount must be a number' };
  }

  if (amount < 0) {
    return { isValid: false, error: 'Amount must be non-negative' };
  }

  // Stripe minimum amounts by currency
  const minimumAmounts: Record<string, number> = {
    USD: 50, // $0.50
    EUR: 50, // €0.50
    GBP: 30, // £0.30
    JPY: 50, // ¥50
    CAD: 50, // C$0.50
    AUD: 50, // A$0.50
  };

  const minAmount = minimumAmounts[currency.toUpperCase()] || 50;

  if (amount < minAmount) {
    return {
      isValid: false,
      error: `Amount must be at least ${minAmount} ${currency.toLowerCase()} cents`,
    };
  }

  return { isValid: true };
};

export const validateCard = (cardDetails: {
  number: string;
  expMonth: number;
  expYear: number;
  cvc: string;
}): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Validate card number (basic Luhn algorithm)
  const cleanNumber = cardDetails.number.replace(/\s/g, '');
  if (!cleanNumber || cleanNumber.length < 13 || cleanNumber.length > 19) {
    errors.push('Card number must be between 13 and 19 digits');
  } else if (!luhnCheck(cleanNumber)) {
    errors.push('Invalid card number');
  }

  // Validate expiration month
  if (
    !cardDetails.expMonth ||
    cardDetails.expMonth < 1 ||
    cardDetails.expMonth > 12
  ) {
    errors.push('Expiration month must be between 1 and 12');
  }

  // Validate expiration year
  const currentYear = new Date().getFullYear();
  if (
    !cardDetails.expYear ||
    cardDetails.expYear < currentYear ||
    cardDetails.expYear > currentYear + 20
  ) {
    errors.push('Invalid expiration year');
  }

  // Check if card is expired
  if (cardDetails.expMonth && cardDetails.expYear) {
    const expirationDate = new Date(
      cardDetails.expYear,
      cardDetails.expMonth - 1,
      1
    );
    const currentDate = new Date();
    if (expirationDate < currentDate) {
      errors.push('Card has expired');
    }
  }

  // Validate CVC
  if (
    !cardDetails.cvc ||
    cardDetails.cvc.length < 3 ||
    cardDetails.cvc.length > 4
  ) {
    errors.push('CVC must be 3 or 4 digits');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Luhn algorithm for credit card validation
const luhnCheck = (cardNumber: string): boolean => {
  let sum = 0;
  let alternate = false;

  for (let i = cardNumber.length - 1; i >= 0; i--) {
    let n = parseInt(cardNumber.charAt(i), 10);

    if (alternate) {
      n *= 2;
      if (n > 9) {
        n = (n % 10) + 1;
      }
    }

    sum += n;
    alternate = !alternate;
  }

  return sum % 10 === 0;
};
